/**
 * The visible hardware: the 30 mm autocannon and the depth-charge projector, mounted on
 * sponsons flanking the player's stance.
 *
 * The plan (§D, common infrastructure) has always required each weapon to have a
 * physical mount on the rig model. Until now only the *ballistics* had one — shells and
 * charges spawned from bare coordinates six metres under the deck, where nothing could
 * ever be seen. Two products carry this game, and one of them is the weapons; a weapon
 * you never see fire is spending its satisfaction on nothing.
 *
 * Placement is Adam's, and it is spatial rather than framing-driven. A first attempt put
 * both on sponsons cantilevered forward of the rail — inside the wide frame while
 * fighting straight ahead — and he rejected it: the weapons belong on the rig's real
 * structure, where a crew would actually stand them.
 *
 *   - **The 30 mm** stands on the observation platform itself, immediately to the
 *     player's west (their left when facing north, the default heading), on a pad
 *     raised one metre so the mount clears the deck and its trunnion sits at eye level.
 *     You turn your head to it, the way you would turn to a gun beside you.
 *   - **The projector** stands on the north-west corner of the crane tower's lower
 *     standing platform — 25 m below and 20 m north-east, lobbing from a deck that is
 *     already outboard and low, which is where a charge thrower wants to be.
 *
 * Compass, per the project convention: north +Z, south -Z, east +X, west -X; port is west.
 *
 * Everything lives in this one file — geometry, materials and motion — deliberately:
 * `rigBuilder.ts` is 6000 lines under active parallel edit, and a self-contained module
 * that only *reads* its DECK_Y cannot collide with it.
 *
 * Draw-call discipline (plan §G): the static pad/projector dressing remains accumulated,
 * while the gun is split only where motion or material demands it — yawing naval-grey
 * body, pitching painted sleeve, recoiling dark mechanism, readable warning placard.
 * That is the minimum split that lets the weapon explain how it works.
 */

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreateBoxVertexData } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinderVertexData } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreatePlaneVertexData } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { PBRMetallicRoughnessMaterial } from '@babylonjs/core/Materials/PBR/pbrMetallicRoughnessMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { DECK_Y } from './rigBuilder';
import { createRigMaterials } from './rigMaterials';
import { createBeveledBoxVertexData } from './beveledBox';
import { createPhysicalUvs, type RigPrimitiveKind } from './physicalUvs';
import { GUN_MOUNT, PROJECTOR_MOUNT } from '../../content/weapons';
import type { WeaponId } from '../../sim/types';

/** The gun's pad: one metre proud of the observation deck, per Adam. */
const GUN_PAD_RISE = 1.0;
const GUN_PAD_TOP = DECK_Y + GUN_PAD_RISE;
const GUN_PAD_HALF = 1.5;

/** Pedestal and trunnion, sized so the barrel's axis lands at the player's eye line. */
const GUN_PEDESTAL = 0.4;
const GUN_TRUNNION = 0.6;

/**
 * The crane tower's lowest ring gallery — the walkway the projector stands on.
 *
 * Derived rather than copied: rigBuilder lays the gallery plate at MAIN_DECK_Y + 6 with
 * a 0.32 thickness, then lowers the whole tower 18 m as one unit. In terms of the deck
 * height this module already imports, that is DECK_Y - (2 × 3.5) + 6 + 0.16 - 18. If
 * the deck moves the mount follows it; if the tower's drop changes, correct it here.
 */
const CRANE_DECK_TOP = DECK_Y - 18.84;
const PROJECTOR_PLINTH = 0.45;

/**
 * Slew rates, radians per second.
 *
 * A mount that snaps to the aim reads as a cursor; one that *chases* reads as several
 * tonnes of machinery being driven. Whipping the view leaves the gun visibly catching
 * up, which is most of what sells it as a real object.
 */
const GUN_TRAVERSE = 1.05;
const GUN_ELEVATE = 0.82;
const GUN_TRAVERSE_ACCEL = 2.1;
const GUN_ELEVATE_ACCEL = 1.75;
const PROJECTOR_TRAVERSE = 1.0;

/** Recoil travel in metres, and the time constant of the return. */
const GUN_RECOIL_TRAVEL = 0.1;
const GUN_RECOIL_TAU = 0.075;
const PROJECTOR_KICK_TRAVEL = 0.1;
const PROJECTOR_KICK_TAU = 0.12;

/** The projector sits at a fixed high angle; the player manages range, not elevation. */
const PROJECTOR_ELEVATION = (55 * Math.PI) / 180;

/**
 * Texture density, tiles per metre, matched to the rig's own accumulators so a scan on
 * the gun reads at the same physical scale as the same scan on the structure behind it.
 */
const DARK_TILES = 0.13;
const YELLOW_TILES = 0.32;
const GUN_PAINT_TILES = 0.18;

/**
 * Accumulates transformed primitives into one buffer — a lean cousin of the rig's own
 * MeshAccumulator, carrying the parts of it that matter here: real normals under
 * non-uniform scale, and physical UVs so the scanned materials land at true size.
 */
class PartBuffer {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly uvs: number[] = [];
  private readonly indices: number[] = [];

  constructor(private readonly tilesPerMetre: number) {}

  add(data: VertexData, transform: Matrix, kind: RigPrimitiveKind, uvScale: number): void {
    const base = this.positions.length / 3;
    // Use the rig's shared physically-sized chamfer on every fabricated box. A six
    // centimetre cabinet edge and an eight millimetre feed-link edge therefore catch
    // light at their own scale instead of sharing one arbitrary modelling bevel.
    const source = kind === 'box' ? createBeveledBoxVertexData(transform).data : data;
    const pos = source.positions as number[];
    const nrm = source.normals as number[];
    const idx = source.indices as number[];

    // Rotation only: the translation must not drag the normals with it.
    const normalMatrix = transform.clone();
    normalMatrix.setTranslationFromFloats(0, 0, 0);

    const p = new Vector3();
    const n = new Vector3();
    for (let i = 0; i < pos.length; i += 3) {
      p.set(pos[i] as number, pos[i + 1] as number, pos[i + 2] as number);
      const tp = Vector3.TransformCoordinates(p, transform);
      this.positions.push(tp.x, tp.y, tp.z);

      n.set(nrm[i] as number, nrm[i + 1] as number, nrm[i + 2] as number);
      const tn = Vector3.TransformNormal(n, normalMatrix).normalize();
      this.normals.push(tn.x, tn.y, tn.z);
    }
    this.uvs.push(
      ...createPhysicalUvs(source, transform, kind, this.tilesPerMetre * uvScale),
    );
    for (const v of idx) this.indices.push(base + v);
  }

  /** Axis-aligned box, centred on (cx, cy, cz). */
  box(
    cx: number, cy: number, cz: number,
    sx: number, sy: number, sz: number,
    uvScale = 1,
  ): void {
    this.add(
      unitBox(),
      Matrix.Compose(
        new Vector3(sx, sy, sz), Quaternion.Identity(), new Vector3(cx, cy, cz),
      ),
      'box',
      uvScale,
    );
  }

  /** Box with an explicit rotation, used for braces, cabinet lids and armour facets. */
  rotatedBox(
    center: Vector3,
    size: Vector3,
    rotation: Quaternion,
    uvScale = 1,
  ): void {
    this.add(
      unitBox(),
      Matrix.Compose(size, rotation, center),
      'box',
      uvScale,
    );
  }

  /**
   * Rectangular member spanning two points, with its long axis down local +Z.
   *
   * The exposed ammunition feed is deliberately made of short separated members rather
   * than one smooth hose. At the player's two-metre viewing distance those gaps are what
   * turn a dark arc into the linked flexible chute in Adam's reference.
   */
  beam(a: Vector3, b: Vector3, width: number, height: number, uvScale = 1): void {
    const delta = b.subtract(a);
    const length = delta.length();
    if (length < 1e-5) return;
    const dir = delta.scale(1 / length);
    const up = Math.abs(Vector3.Dot(dir, Vector3.Up())) > 0.94
      ? Vector3.Forward()
      : Vector3.Up();
    this.rotatedBox(
      a.add(b).scale(0.5),
      new Vector3(width, height, length),
      Quaternion.FromLookDirectionLH(dir, up),
      uvScale,
    );
  }

  /** Cylinder spanning a to b. */
  tube(a: Vector3, b: Vector3, radius: number, uvScale = 1): void {
    const delta = b.subtract(a);
    const length = delta.length();
    if (length < 1e-5) return;
    const dir = delta.scale(1 / length);

    // Rotate the unit cylinder's +Y axis onto the span. The cross product degenerates
    // when the two are parallel or opposed, which is the common case here — most of
    // this hardware is axis-aligned — so both are handled explicitly.
    const up = Vector3.Up();
    const dot = Vector3.Dot(up, dir);
    const axis = Vector3.Cross(up, dir);
    const rotation = axis.lengthSquared() < 1e-8
      ? (dot > 0 ? Quaternion.Identity() : Quaternion.RotationAxis(Vector3.Right(), Math.PI))
      : Quaternion.RotationAxis(axis.normalize(), Math.acos(Math.min(1, Math.max(-1, dot))));

    this.add(
      unitCylinder(),
      Matrix.Compose(
        new Vector3(radius * 2, length, radius * 2),
        rotation,
        a.add(b).scale(0.5),
      ),
      'cylinder',
      uvScale,
    );
  }

  get triangleCount(): number {
    return this.indices.length / 3;
  }

  build(scene: Scene, name: string, material: PBRMetallicRoughnessMaterial): Mesh {
    const mesh = new Mesh(name, scene);
    const vd = new VertexData();
    vd.positions = this.positions;
    vd.normals = this.normals;
    vd.uvs = this.uvs;
    vd.indices = this.indices;
    vd.applyToMesh(mesh, false);
    mesh.material = material;
    mesh.isPickable = false;
    mesh.receiveShadows = false;
    return mesh;
  }
}

let boxData: VertexData | null = null;
let cylinderData: VertexData | null = null;

function unitBox(): VertexData {
  boxData ??= CreateBoxVertexData({ size: 1 });
  return boxData;
}

function unitCylinder(): VertexData {
  // Twelve sides: these are hand-sized objects a few metres from the camera, where the
  // silhouette of a barrel matters and its facet count does not.
  cylinderData ??= CreateCylinderVertexData({ height: 1, diameter: 1, tessellation: 12 });
  return cylinderData;
}

/** Shortest signed angular difference, radians. */
function angleDelta(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** Step an angle toward a target at a limited rate. */
function slew(current: number, target: number, rate: number, dt: number): number {
  const delta = angleDelta(current, target);
  const step = rate * dt;
  if (Math.abs(delta) <= step) return target;
  return current + Math.sign(delta) * step;
}

interface SlewState {
  angle: number;
  velocity: number;
}

/**
 * Servo motion with acceleration and braking.
 *
 * The old mount jumped to full slew speed on the first frame and stopped dead on the
 * last. Limiting speed alone made the gun lag, but not feel heavy. Braking against the
 * remaining angle gives the several-hundred-kilogram body a visible wind-up and settle
 * without adding theatrical wobble to a stabilized naval mount.
 */
function inertialSlew(
  current: number,
  target: number,
  velocity: number,
  maxRate: number,
  acceleration: number,
  dt: number,
): SlewState {
  const delta = angleDelta(current, target);
  const stoppingDistance = (velocity * velocity) / (2 * acceleration);
  const desiredVelocity = Math.abs(delta) <= stoppingDistance
    ? 0
    : Math.sign(delta) * maxRate;
  const velocityStep = acceleration * dt;
  const nextVelocity = velocity + Math.max(
    -velocityStep,
    Math.min(velocityStep, desiredVelocity - velocity),
  );
  const nextAngle = current + nextVelocity * dt;

  if (Math.sign(delta) !== Math.sign(angleDelta(nextAngle, target))
    || (Math.abs(delta) < 1e-4 && Math.abs(nextVelocity) <= velocityStep)) {
    return { angle: target, velocity: 0 };
  }
  return { angle: nextAngle, velocity: nextVelocity };
}

export class WeaponMounts {
  private readonly staticDark: Mesh;
  private readonly staticYellow: Mesh;
  private readonly gunYoke: Mesh;
  private readonly gunCradlePaint: Mesh;
  private readonly gunCradle: Mesh;
  private readonly dangerSign: Mesh;
  private readonly projectorTube: Mesh;
  private readonly gunPaint: PBRMetallicRoughnessMaterial;

  private gunYaw = 0;
  private gunPitch = 0;
  private gunYawVelocity = 0;
  private gunPitchVelocity = 0;
  private projectorYaw = 0;
  /** Recoil state, 0..1, decaying toward rest. */
  private gunRecoil = 0;
  private projectorKick = 0;

  readonly triangleCount: number;

  constructor(scene: Scene) {
    /*
     * The rig's own scanned materials, not a flat colour of my own.
     *
     * The first cut used one plain PBR material and read as untextured grey plastic
     * sitting in front of photographic steel — the standing lesson of this project, that
     * when something looks fake you ask what is ABSENT before you tune anything. What
     * was absent was the scan. Materials are re-created rather than borrowed from
     * buildRig because rigBuilder is under parallel edit; Babylon caches internal
     * textures by URL, so the duplicate wrappers share their GPU memory.
     */
    const mats = createRigMaterials(scene);

    const dark = new PartBuffer(DARK_TILES);
    const yellow = new PartBuffer(YELLOW_TILES);

    buildGunPad(dark, yellow, GUN_MOUNT.x, GUN_MOUNT.z);

    // Projector plinth on the crane platform's north-west corner, with its ready
    // charges cradled inboard of it.
    dark.box(
      PROJECTOR_MOUNT.x, CRANE_DECK_TOP + PROJECTOR_PLINTH * 0.5, PROJECTOR_MOUNT.z,
      1.0, PROJECTOR_PLINTH, 1.0, 6,
    );
    yellow.box(
      PROJECTOR_MOUNT.x + 0.95, CRANE_DECK_TOP + 0.3, PROJECTOR_MOUNT.z - 0.55,
      0.5, 0.6, 0.72, 6,
    );

    const yoke = new PartBuffer(GUN_PAINT_TILES);
    const cradlePaint = new PartBuffer(GUN_PAINT_TILES);
    const cradle = new PartBuffer(DARK_TILES);
    buildGunYoke(yoke);
    buildGunCradlePaint(cradlePaint);
    buildGunCradle(cradle);
    const tube = new PartBuffer(DARK_TILES);
    buildProjectorTube(tube);

    this.staticDark = dark.build(scene, 'weaponMountsSteel', mats.darkSteel);
    this.staticYellow = yellow.build(scene, 'weaponMountsPaint', mats.yellowPaint);

    const gunPaint = mats.steel.clone('gunNavalPaint') as
      | PBRMetallicRoughnessMaterial
      | null;
    if (!gunPaint) throw new Error('Failed to clone the 30 mm naval paint material');
    // The worn scan supplies normal/roughness/occlusion. Its dark colour map belongs on
    // exposed structural steel, not on the pale sealed naval housings in Adam's reference.
    gunPaint.baseTexture = null;
    gunPaint.baseColor = new Color3(0.32, 0.39, 0.43);
    gunPaint.metallic = 0.06;
    gunPaint.roughness = 0.82;
    this.gunPaint = gunPaint;

    /*
     * The reference's appeal is the readable split between pale sealed machinery and
     * the exposed dark cannon. Keeping those roles on separate meshes prevents the
     * receiver, feed and barrel collapsing back into one black rectangular mass.
     */
    this.gunYoke = yoke.build(scene, 'gunYoke', gunPaint);
    this.gunCradlePaint = cradlePaint.build(scene, 'gunCradlePaint', gunPaint);
    this.gunCradle = cradle.build(scene, 'gunCradle', mats.machinedSteel);
    this.projectorTube = tube.build(scene, 'projectorTube', mats.machinedSteel);

    // The yoke yaws about the pedestal's vertical axis; the cradle rides it and pitches
    // about the trunnion, so aiming composes exactly the way the real mount does.
    this.gunYoke.position.set(GUN_MOUNT.x, GUN_PAD_TOP + GUN_PEDESTAL, GUN_MOUNT.z);
    this.gunCradlePaint.parent = this.gunYoke;
    this.gunCradlePaint.position.set(0, GUN_TRUNNION, 0);
    this.gunCradle.parent = this.gunYoke;
    this.gunCradle.position.set(0, GUN_TRUNNION, 0);
    this.dangerSign = buildDangerSign(scene, this.gunYoke);

    this.projectorTube.position.set(
      PROJECTOR_MOUNT.x, CRANE_DECK_TOP + PROJECTOR_PLINTH, PROJECTOR_MOUNT.z,
    );

    this.triangleCount = dark.triangleCount + yellow.triangleCount + yoke.triangleCount
      + cradlePaint.triangleCount + cradle.triangleCount + tube.triangleCount
      + (this.dangerSign.getTotalIndices() / 3);

    registerWeaponShadows(scene, [
      this.staticDark,
      this.staticYellow,
      this.gunYoke,
      this.gunCradlePaint,
      this.gunCradle,
      this.projectorTube,
    ], this.dangerSign);
  }

  /** Every mesh, for anything that wants to count or light them. */
  get meshes(): Mesh[] {
    return [
      this.staticDark, this.staticYellow,
      this.gunYoke, this.gunCradlePaint, this.gunCradle,
      this.dangerSign, this.projectorTube,
    ];
  }

  /**
   * Track the player's aim.
   *
   * The gun follows the bearing at all times — it is the weapon the eye lives with, and
   * hardware that freezes when you switch weapons reads as broken. The projector only
   * trains while it is selected: it lobs at a point on the water, and a mortar swinging
   * around after a look the player is not shooting with is noise.
   */
  update(dt: number, aim: { x: number; y: number; z: number }, selected: WeaponId): void {
    const flat = Math.hypot(aim.x, aim.z);
    if (flat > 1e-4) {
      const bearing = Math.atan2(aim.x, aim.z);
      const yaw = inertialSlew(
        this.gunYaw,
        bearing,
        this.gunYawVelocity,
        GUN_TRAVERSE,
        GUN_TRAVERSE_ACCEL,
        dt,
      );
      this.gunYaw = yaw.angle;
      this.gunYawVelocity = yaw.velocity;
      if (selected === 'depthCharge') {
        this.projectorYaw = slew(this.projectorYaw, bearing, PROJECTOR_TRAVERSE, dt);
      }
      // Babylon pitches nose-down on positive rotation.x, so an elevated barrel is a
      // negative angle — the same convention the camera uses.
      const elevation = Math.atan2(aim.y, flat);
      const pitch = inertialSlew(
        this.gunPitch,
        -elevation,
        this.gunPitchVelocity,
        GUN_ELEVATE,
        GUN_ELEVATE_ACCEL,
        dt,
      );
      this.gunPitch = pitch.angle;
      this.gunPitchVelocity = pitch.velocity;
    }

    this.gunRecoil *= Math.exp(-dt / GUN_RECOIL_TAU);
    this.projectorKick *= Math.exp(-dt / PROJECTOR_KICK_TAU);

    this.gunYoke.rotation.y = this.gunYaw;
    this.gunCradlePaint.rotation.x = this.gunPitch;
    this.gunCradle.rotation.x = this.gunPitch;
    // Only the cannon and its painted recoil sleeve move. The base, ammunition cabinet
    // and trunnion stay planted, which is where the shot gets its apparent weight.
    this.gunCradlePaint.position.z = -this.gunRecoil * GUN_RECOIL_TRAVEL;
    this.gunCradle.position.z = -this.gunRecoil * GUN_RECOIL_TRAVEL;

    this.projectorTube.rotation.y = this.projectorYaw;
    this.projectorTube.position.y = CRANE_DECK_TOP + PROJECTOR_PLINTH
      - this.projectorKick * PROJECTOR_KICK_TRAVEL;
  }

  /** One round away: kick the cradle back. At 300 rpm this reads as a judder. */
  gunFired(): void {
    this.gunRecoil = 1;
  }

  /** A charge lobbed: the projector sits down on its plinth and recovers. */
  projectorFired(): void {
    this.projectorKick = 1;
  }

  dispose(): void {
    const signMaterial = this.dangerSign.material;
    for (const mesh of this.meshes) mesh.dispose();
    signMaterial?.dispose(true, true);
    this.gunPaint.dispose();
  }
}

/**
 * The gun's raised pad, standing on the observation deck rather than cantilevered off
 * it: a one-metre plinth, a kerb to stop a boot going over the edge, and a step up on
 * the east face — the side the player walks from.
 */
function buildGunPad(dark: PartBuffer, yellow: PartBuffer, cx: number, cz: number): void {
  const half = GUN_PAD_HALF;
  dark.box(cx, DECK_Y + GUN_PAD_RISE * 0.5, cz, half * 2, GUN_PAD_RISE, half * 2, 5);

  // Kerb on the three outboard sides; the east face is the way up and stays clear.
  yellow.box(cx, GUN_PAD_TOP + 0.09, cz + half - 0.06, half * 2, 0.18, 0.12, 3);
  yellow.box(cx - half + 0.06, GUN_PAD_TOP + 0.09, cz, 0.12, 0.18, half * 2, 3);
  yellow.box(cx, GUN_PAD_TOP + 0.09, cz - half + 0.06, half * 2, 0.18, 0.12, 3);

  // The step up on the east face — the side the player walks from. No grab rail: the
  // first cut put a bright vertical pole squarely between the stance and the gun.
  dark.box(cx + half + 0.28, DECK_Y + 0.25, cz, 0.56, 0.5, 1.3, 6);
}

/**
 * The yawing body: bolted slew ring, drive housing, open support cheeks and the
 * player-facing feed cabinet.
 *
 * It is deliberately asymmetrical. Real machinery has a reason for every lump; the
 * reference's large ammunition/drive cabinet on one side is what stops the silhouette
 * reading as a generic sci-fi turret. Here it faces east, toward Adam at the stance.
 */
function buildGunYoke(buf: PartBuffer): void {
  // Bolted circular slew ring and the sealed motor housing above it.
  buf.tube(
    new Vector3(0, -GUN_PEDESTAL, 0),
    new Vector3(0, -GUN_PEDESTAL + 0.13, 0),
    0.76,
    5,
  );
  buf.tube(
    new Vector3(0, -GUN_PEDESTAL + 0.13, 0),
    new Vector3(0, -0.05, 0),
    0.63,
    5,
  );
  buf.tube(new Vector3(0, -0.05, 0), new Vector3(0, 0.04, 0), 0.68, 6);

  // Radial feet make the ring look anchored into the one-metre ammunition plinth.
  for (let i = 0; i < 8; i += 1) {
    const a = (i / 8) * Math.PI * 2;
    buf.rotatedBox(
      new Vector3(Math.sin(a) * 0.66, -GUN_PEDESTAL + 0.03, Math.cos(a) * 0.66),
      new Vector3(0.18, 0.17, 0.3),
      Quaternion.RotationAxis(Vector3.Up(), a),
      7,
    );
  }

  // Cooling ribs and access-door relief on the east face, where the player sees them.
  for (let i = -2; i <= 2; i += 1) {
    buf.box(0.645, -0.2, i * 0.105, 0.055, 0.23, 0.045, 10);
  }

  // Open cheeks carry the trunnion. Negative space between them is as important as the
  // steel: it exposes the receiver rather than returning to the old solid block.
  for (const side of [-1, 1]) {
    buf.box(side * 0.45, 0.28, 0.02, 0.16, 0.54, 0.48, 7);
    buf.beam(
      new Vector3(side * 0.45, 0.03, -0.22),
      new Vector3(side * 0.45, GUN_TRUNNION - 0.02, 0.02),
      0.17,
      0.14,
      7,
    );
  }

  // Large trunnion drum, the visible hinge the old mount was missing.
  buf.tube(
    new Vector3(-0.59, GUN_TRUNNION, 0.02),
    new Vector3(0.59, GUN_TRUNNION, 0.02),
    0.27,
    6,
  );

  // Feed/drive cabinet on the player-facing side. The 1,800-round drum lives down in
  // the raised plinth; this is the credible ready-feed and electronics housing above it.
  buf.box(0.76, 0.24, -0.18, 0.58, 0.57, 0.76, 5);
  buf.rotatedBox(
    new Vector3(0.76, 0.555, -0.18),
    new Vector3(0.64, 0.12, 0.82),
    Quaternion.RotationAxis(Vector3.Right(), -0.055),
    5,
  );
  buf.box(0.76, 0.05, -0.575, 0.5, 0.18, 0.08, 8);
  // Raised door rails catch a thin highlight and break up the cabinet's large face.
  buf.box(1.058, 0.49, -0.18, 0.045, 0.045, 0.67, 9);
  buf.box(1.058, 0.1, -0.18, 0.045, 0.045, 0.67, 9);
}

/**
 * Pale parts which pitch and recoil with the cannon: the bearing sleeve and lower tray.
 * Keeping them separate from the dark receiver makes the barrel's load path legible.
 */
function buildGunCradlePaint(buf: PartBuffer): void {
  buf.box(0, -0.16, -0.22, 0.7, 0.15, 0.92, 7);
  buf.tube(new Vector3(0, 0, 0.04), new Vector3(0, 0, 0.76), 0.2, 7);
  buf.tube(new Vector3(0, 0, 0.68), new Vector3(0, 0, 0.86), 0.235, 8);
}

/**
 * The pitching dark mechanism: receiver, optics, barrel, muzzle brake, hoses and the
 * exposed flexible ammunition feed.
 *
 * Built around the trunnion at the local origin with the barrel down +Z, so the mesh's
 * own rotation is the gun's elevation and nothing needs an offset pivot.
 */
function buildGunCradle(buf: PartBuffer): void {
  // Receiver and chain-drive cover behind the trunnion.
  buf.box(0, 0.02, -0.41, 0.54, 0.4, 0.88, 6);
  buf.box(0, 0.235, -0.48, 0.46, 0.15, 0.58, 7);
  buf.box(0.27, -0.015, -0.43, 0.16, 0.3, 0.48, 8);

  // Coaxial day/thermal camera body and a hooded forward lens.
  buf.box(0.35, 0.14, -0.57, 0.18, 0.22, 0.3, 9);
  buf.tube(
    new Vector3(0.35, 0.14, -0.44),
    new Vector3(0.35, 0.14, -0.32),
    0.075,
    10,
  );

  // Recoil guides visible beneath the receiver, and the downward ejection chute.
  for (const x of [-0.18, 0.18]) {
    buf.tube(
      new Vector3(x, -0.17, -0.7),
      new Vector3(x, -0.17, 0.2),
      0.022,
      10,
    );
  }
  buf.rotatedBox(
    new Vector3(-0.24, -0.25, -0.38),
    new Vector3(0.18, 0.24, 0.34),
    Quaternion.RotationAxis(Vector3.Right(), -0.18),
    8,
  );

  // The visible cannon: a reinforced root, long 30 mm barrel and restrained brake.
  buf.tube(new Vector3(0, 0, 0.72), new Vector3(0, 0, 1.03), 0.13, 7);
  buf.tube(new Vector3(0, 0, 0.96), new Vector3(0, 0, 2.69), 0.06, 8);
  buf.tube(new Vector3(0, 0, 2.62), new Vector3(0, 0, 2.82), 0.1, 9);
  buf.tube(new Vector3(0, 0, 2.8), new Vector3(0, 0, 2.87), 0.07, 10);

  buildAmmunitionFeed(buf);

  // Flexible hydraulic/power loop below the receiver.
  const hose = [
    new Vector3(0.29, -0.08, -0.5),
    new Vector3(0.41, -0.21, -0.42),
    new Vector3(0.48, -0.3, -0.22),
    new Vector3(0.46, -0.29, 0.0),
    new Vector3(0.36, -0.2, 0.14),
  ];
  for (let i = 0; i < hose.length - 1; i += 1) {
    buf.tube(hose[i] as Vector3, hose[i + 1] as Vector3, 0.026, 10);
  }
}

/** Linked black feed chute arcing from the east cabinet into the receiver. */
function buildAmmunitionFeed(buf: PartBuffer): void {
  const start = new Vector3(0.79, -0.04, -0.06);
  const control = new Vector3(0.84, 0.52, -0.43);
  const end = new Vector3(0.23, 0.29, -0.79);
  const links = 20;

  const point = (t: number): Vector3 => {
    const u = 1 - t;
    return start.scale(u * u)
      .add(control.scale(2 * u * t))
      .add(end.scale(t * t));
  };

  for (let i = 0; i < links; i += 1) {
    const a = point(i / links);
    const b = point((i + 1) / links);
    const middle = a.add(b).scale(0.5);
    const half = b.subtract(a).scale(0.34);
    buf.beam(middle.subtract(half), middle.add(half), 0.13, 0.075, 12);
  }
}

/**
 * Player-facing warning plate on the feed cabinet.
 *
 * This is a texture rather than tiny letter geometry so "DANGER" stays genuinely
 * readable in the normal head-turn benchmark. It is original project artwork generated
 * from the local SVG; Adam's supplied photograph is a design reference, not a shipped
 * asset.
 */
function buildDangerSign(scene: Scene, parent: Mesh): Mesh {
  const sign = new Mesh('gunDangerSign', scene);
  CreatePlaneVertexData({
    width: 0.55,
    height: 0.22,
    sideOrientation: Mesh.DOUBLESIDE,
  }).applyToMesh(sign, false);

  const texture = new DynamicTexture(
    'gunDangerSignTexture',
    { width: 1100, height: 440 },
    scene,
    false,
  );
  const ctx = texture.getContext() as unknown as CanvasRenderingContext2D;
  ctx.fillStyle = '#f2c300';
  ctx.fillRect(0, 0, 1100, 440);
  ctx.strokeStyle = '#151719';
  ctx.lineWidth = 28;
  ctx.strokeRect(14, 14, 1072, 412);

  ctx.beginPath();
  ctx.moveTo(128, 83);
  ctx.lineTo(34, 257);
  ctx.lineTo(222, 257);
  ctx.closePath();
  ctx.lineWidth = 22;
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.fillStyle = '#151719';
  ctx.fillRect(117, 133, 22, 70);
  ctx.beginPath();
  ctx.arc(128, 229, 13, 0, Math.PI * 2);
  ctx.fill();

  ctx.font = '900 174px Arial, Helvetica, sans-serif';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('DANGER', 268, 236);
  ctx.fillRect(44, 292, 1012, 5);
  ctx.font = '700 58px Arial, Helvetica, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('REMOTE MOUNT — KEEP CLEAR', 550, 367);
  texture.update(false);
  texture.gammaSpace = true;
  const material = new PBRMetallicRoughnessMaterial('gunDangerSignMaterial', scene);
  material.baseTexture = texture;
  material.baseColor = Color3.White();
  material.metallic = 0.02;
  material.roughness = 0.86;
  material.backFaceCulling = false;

  sign.material = material;
  sign.parent = parent;
  // The cabinet's east face points directly toward the player's stance.
  sign.position.set(1.055, 0.3, -0.18);
  sign.rotation.y = Math.PI / 2;
  sign.rotation.z = Math.PI;
  sign.isPickable = false;
  sign.receiveShadows = true;
  return sign;
}

/**
 * Mounts are created after the rig's shadow map has taken its initial caster list.
 * Registering them here gives the base contact, self-shadow and overhead shade that
 * stops pale machinery reading as a flat pasted-on prop.
 */
function registerWeaponShadows(scene: Scene, casters: Mesh[], receiver: Mesh): void {
  receiver.receiveShadows = true;
  for (const mesh of casters) mesh.receiveShadows = true;

  for (const light of scene.lights) {
    const list = light.getShadowGenerator()?.getShadowMap()?.renderList;
    if (!list) continue;
    for (const mesh of casters) {
      if (!list.includes(mesh)) list.push(mesh);
    }
  }
}

/** The projector: trunnion cheeks, the stubby tube at its fixed angle, and the drum. */
function buildProjectorTube(buf: PartBuffer): void {
  for (const side of [-1, 1]) {
    buf.box(side * 0.27, 0.2, -0.05, 0.1, 0.4, 0.4, 9);
  }
  const base = new Vector3(0, 0.16, -0.22);
  const tip = base.add(new Vector3(
    0, Math.sin(PROJECTOR_ELEVATION) * 1.32, Math.cos(PROJECTOR_ELEVATION) * 1.32,
  ));
  buf.tube(base, tip, 0.21, 8);
  // Blast collar at the muzzle, and the ready magazine drum alongside.
  buf.tube(tip.subtract(new Vector3(0, 0.12, 0.08)), tip, 0.27, 9);
  buf.tube(new Vector3(-0.52, 0.3, -0.16), new Vector3(-0.18, 0.3, -0.16), 0.3, 8);
}
